// ═══════════════════════════════════════════════════════════════════════════
// centralPromosCanonicoShape — canónicas → shapes legacy de CentralPromociones
// (Wave C C6d). PURO — sin React, sin Firestore.
// ═══════════════════════════════════════════════════════════════════════════
//
// CentralPromocionesDashboard consume 5 colecciones legacy. Con el flag
// `feature.promos.canonico` ON, el hook useCentralPromosData lee las
// canónicas y las mapea acá al MISMO shape que espera el JSX/agregación
// (buildVistaCampanias, familias, banner) — el dashboard no se toca.
//
//   mlPromos               ← campanas (tipo='promo')         → campanaPromoALegacyMaster
//   mlPublicacionesPromos  ← campanasItems (subtipo!='ads')  → campanasItemsAPromosPorItem
//   mlPromosItems          ← (vacío: la canónica es single-source pub-side;
//                             el fallback por campaña no aplica)
//   mlPromosExclusiones    ← empresas.promosExclusionSeller  → exclusionDesdeEmpresa
//   mlPublicaciones        ← publicaciones (espejo pubCanonicaALegacy + extras)
//
// ⛔ GATE DE FLIP (lente B2): NO prender `feature.promos.canonico` hasta que
// `campanas-promo` tenga sweep/_visible (el legacy marcaba _visible:false las
// campañas que ML dejó de listar; el canónico todavía no → con ON una campaña
// retirada quedaría fantasma para siempre). Tajada siguiente de C6d.
//
// DIVERGENCIAS DECLARADAS:
// 1. (exclusiones) la lista per-item NO existe en ML v2 (C3b: endpoint
//    per-user 404; mlPromosExclusiones vacía en staging+prod desde siempre).
//    El banner legacy "N items autoexcluidos" NUNCA se mostró. La canónica
//    expone el estado SELLER-level ({excluded|not_excluded}) → el banner pasa
//    a "el seller está autoexcluido de promociones automáticas".
// 2. (sub_type/deadline_date del array promociones) el adapter canónico
//    campanas-items NO persiste sub_type/deadline_date/top_deal_price de cada
//    participación (el legacy guardaba el array RAW). Impacto: solo buckets
//    sembrados desde pub-primary SIN campaña en el master pierden esos 2
//    campos (el master los trae vía datosCanal.subType/vigencia.deadline).
//    Si duele, el fix es persistirlos en el adapter (no acá).
// 3. (conteo "promos por item") el legacy persistía 1 doc por pub AUNQUE no
//    tuviera promos (promociones:[]); la canónica solo materializa pubs con
//    ≥1 participación → el contador del header baja al flip (más honesto).
// ═══════════════════════════════════════════════════════════════════════════

import { pubCanonicaALegacy } from './publicacionesLegacyShape'

/**
 * Doc canónico `campanas` (tipo='promo', nucleo APLANADO) → shape master
 * mlPromos que siembra buildVistaCampanias (promotionId, name, type, ...).
 */
export function campanaPromoALegacyMaster(c) {
  if (!c) return null
  return {
    promotionId: c.externalId ?? null,
    conexionId: c.conexionId ?? null,
    name: c.nombre ?? null,
    type: c.subtipo ?? null,
    sub_type: c.datosCanal?.subType ?? null,
    status: c.estado ?? null,
    start_date: c.vigencia?.desde ?? null,
    finish_date: c.vigencia?.hasta ?? null,
    deadline_date: c.vigencia?.deadline ?? null,
    benefits: c.datosCanal?.benefits ?? null,
    // Sweep de visibilidad (gate B2): el master canónico marca _visible:false
    // las campañas que ML dejó de listar — buildVistaCampanias las saltea
    // (`=== false`; ausente = visible, igual que el legacy).
    _visible: c._visible ?? null,
    _syncedAt: c._syncedAt ?? null,
  }
}

/**
 * Doc canónico `campanasItems` (participación de promo, nucleo APLANADO) →
 * entrada del array `promociones[]` legacy que consume `acumular` (id, status,
 * price, meli_percentage, min/max/suggested_discounted_price, ...).
 * Nota: PRICE_DISCOUNT viene con campanaExternalId null → id null; `acumular`
 * lo saltea (`if (!p?.id) return`), MISMO comportamiento que el legacy
 * (la API tampoco le da id de campaña). Paridad, no pérdida.
 */
export function participacionALegacyPromo(ci) {
  if (!ci) return null
  return {
    id: ci.campanaExternalId ?? null,
    type: ci.subtipo ?? null,
    status: ci.estado ?? null,
    price: ci.precios?.promo ?? null,
    original_price: ci.precios?.original ?? null,
    min_discounted_price: ci.precios?.min ?? null,
    max_discounted_price: ci.precios?.max ?? null,
    suggested_discounted_price: ci.precios?.sugerido ?? null,
    meli_percentage: ci.porcentajes?.meli ?? null,
    seller_percentage: ci.porcentajes?.seller ?? null,
    name: ci.datosCanal?.name ?? null,
    start_date: ci.datosCanal?.startDate ?? null,
    finish_date: ci.datosCanal?.finishDate ?? null,
  }
}

/**
 * campanasItems (mezclados) → docs shape mlPublicacionesPromos: 1 doc por
 * publicación con su array `promociones[]`. Filtra subtipo='ads' (mismo
 * candado que la bitácora). conexionId/_syncedAt: del doc más reciente.
 *
 * @param {Array} campanasItems — docs canónicos crudos
 * @returns {Array<{itemId, conexionId, promociones, _syncedAt}>}
 */
export function campanasItemsAPromosPorItem(campanasItems = []) {
  const porPub = new Map()
  for (const ci of (campanasItems || [])) {
    if (!ci || ci.subtipo === 'ads') continue
    const itemId = ci.publicacionExternalId
    if (!itemId) continue
    if (!porPub.has(itemId)) {
      porPub.set(itemId, { itemId: String(itemId), conexionId: ci.conexionId ?? null, promociones: [], _syncedAt: ci._syncedAt ?? null })
    }
    const bucket = porPub.get(itemId)
    bucket.promociones.push(participacionALegacyPromo(ci))
    const ts = ci._syncedAt?.toMillis?.() ?? 0
    const prev = bucket._syncedAt?.toMillis?.() ?? 0
    if (ts > prev) { bucket._syncedAt = ci._syncedAt; bucket.conexionId = ci.conexionId ?? bucket.conexionId }
  }
  return [...porPub.values()]
}

/**
 * Denorm `empresas.promosExclusionSeller` ({estado, conexionId, actualizadoEn})
 * → shape para el banner. `cantidad` legacy no existe más (sin fuente en ML
 * v2): el banner canónico es seller-level.
 *
 * @returns {{ sellerExcluido:boolean, estado:string|null, conexionId, actualizadoEn }|null}
 */
export function exclusionDesdeEmpresa(empresaData) {
  const e = empresaData?.promosExclusionSeller
  if (!e) return null
  return {
    sellerExcluido: e.estado === 'excluded',
    estado: e.estado ?? null,
    conexionId: e.conexionId ?? null,
    actualizadoEn: e.actualizadoEn ?? null,
  }
}

/**
 * Doc canónico `publicaciones` → shape mlPublicaciones que consume el
 * dashboard (familias/variaciones/itemById). Reusa el espejo compartido
 * pubCanonicaALegacy y suma los 3 campos que CentralPromos necesita y el
 * espejo no expone: familyId (agrupador de variaciones), conexionId
 * (filtro multicuenta) y _syncedAt (filtro anti-zombie).
 */
export function pubCanonicaACentralPromos(p) {
  if (!p) return null
  return {
    ...pubCanonicaALegacy(p),
    familyId: p.datosCanal?.familyId ?? null,
    conexionId: p.conexionId ?? null,
    _syncedAt: p._syncedAt ?? null,
  }
}
